import { Component, OnDestroy, OnInit, TemplateRef, ChangeDetectionStrategy, inject, output, viewChild, input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@environments/environment';
import { getToken, IAuthenticationState } from '@app/stores';
import { IframeModalShellComponent } from '../iframe-modal-shell/iframe-modal-shell.component';

function tryParseMessageData(raw: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export interface SbFontSelection {
  fontFamily: string;
  fontFace?: string | null;
}

@Component({
  selector: 'app-font-browser',
  templateUrl: './font-browser.component.html',
  styleUrls: ['./font-browser.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IframeModalShellComponent]
})
export class FontBrowserComponent implements OnInit, OnDestroy {
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);
  private toastr = inject(ToastrService);

  unsubscriber = new Subject<void>();
  token: string | null = null;
  browserUrl: SafeResourceUrl | null = null;
  nestedFileUrl: SafeResourceUrl | null = null;

  readonly projectId = input<number | null>(null);
  readonly iconOnly = input(true);
  readonly disabled = input(false);
  readonly defaultIcon = input('fa fa-font');

  readonly fontSelected = output<SbFontSelection>();

  readonly browserModal = viewChild<TemplateRef<unknown>>('browserModal');
  readonly nestedFileModal = viewChild<TemplateRef<unknown>>('nestedFileModal');

  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private modalRef: NgbModalRef | null = null;
  private nestedModalRef: NgbModalRef | null = null;
  private nestedFileSource: WindowProxy | null = null;

  ngOnInit(): void {
    this.authenticationStore
      .pipe(select(getToken), takeUntil(this.unsubscriber))
      .subscribe((newToken) => {
        if (newToken) {
          this.token = newToken;
        }
      });
  }

  openFontBrowser(): void {
    if (!this.token) {
      this.toastr.warning('Please wait until you are authenticated, then try again', 'Font browser');
      return;
    }
    const theme = document.body.classList.contains('dark-theme') ? 'dark' : 'light';
    const appContext = { token: this.token, projectId: this.projectId() || null };
    const url =
      `${environment.apiUrl}/share/font-browser/show` +
      `?appcontext=${encodeURIComponent(JSON.stringify(appContext))}` +
      `&theme=${theme}`;
    this.browserUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    this.bindMessageHandler();
    this.modalRef = this.modalService.open(this.browserModal(), {
      centered: false,
      size: 'xl',
      keyboard: true,
      windowClass: 'snoball-iframe-modal-window snoball-browser-modal',
      backdropClass: 'snoball-browser-modal-backdrop',
    });
    this.modalRef.result.then(
      () => this.cleanupModal(),
      () => this.cleanupModal(),
    );
  }

  closeModal(): void {
    this.modalRef?.dismiss('cancel');
  }

  closeNestedFileModal(): void {
    this.nestedModalRef?.dismiss('cancel');
  }

  private bindMessageHandler(): void {
    this.unbindMessageHandler();
    this.messageHandler = (event: MessageEvent) => {
      const data =
        typeof event.data === 'string' ? tryParseMessageData(event.data) : (event.data as Record<string, unknown>);
      if (!data) {
        return;
      }
      if (data.origin === 'fontBrowser' && data.action === 'fontSelected') {
        const fontFamily = String(data.fontFamily || '');
        if (fontFamily) {
          this.fontSelected.emit({
            fontFamily,
            fontFace: (data.fontFace as string) || (data.fontStyle as string) || null,
          });
        }
        this.closeModal();
        return;
      }
      if (data.origin === 'fontBrowser' && data.action === 'close') {
        this.closeModal();
        return;
      }
      if (data.origin === 'fontBrowser' && data.action === 'openFileBrowser') {
        this.openNestedFileBrowser(event.source as WindowProxy | null);
        return;
      }
      if (data.origin === 'fileBrowser') {
        if (data.action === 'fileSelected') {
          const raw =
            data.url ?? data.fileUrl ?? data.href ?? data.src ?? (data.payload as Record<string, unknown>)?.url;
          const fileUrl = typeof raw === 'string' ? raw : '';
          if (fileUrl && this.nestedFileSource) {
            this.nestedFileSource.postMessage(
              { origin: 'parent', action: 'fontFileSelected', url: fileUrl },
              '*',
            );
          }
          this.closeNestedFileModal();
        } else if (data.action === 'close') {
          this.closeNestedFileModal();
        }
      }
    };
    window.addEventListener('message', this.messageHandler);
  }

  private openNestedFileBrowser(source: WindowProxy | null): void {
    if (!this.token) {
      return;
    }
    this.nestedFileSource = source;
    const theme = document.body.classList.contains('dark-theme') ? 'dark' : 'light';
    const appContext = { token: this.token, projectId: this.projectId() || null };
    const url =
      `${environment.apiUrl}/share/file-browser/show` +
      `?appcontext=${encodeURIComponent(JSON.stringify(appContext))}` +
      `&theme=${theme}`;
    this.nestedFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.nestedModalRef = this.modalService.open(this.nestedFileModal(), {
      centered: false,
      size: 'xl',
      keyboard: true,
      windowClass: 'snoball-iframe-modal-window snoball-browser-modal',
      backdropClass: 'snoball-browser-modal-backdrop',
    });
    this.nestedModalRef.result.then(
      () => {
        this.nestedFileUrl = null;
        this.nestedFileSource = null;
        this.nestedModalRef = null;
      },
      () => {
        this.nestedFileUrl = null;
        this.nestedFileSource = null;
        this.nestedModalRef = null;
      },
    );
  }

  private unbindMessageHandler(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
  }

  private cleanupModal(): void {
    this.unbindMessageHandler();
    this.modalRef = null;
    this.browserUrl = null;
  }

  ngOnDestroy(): void {
    this.cleanupModal();
    this.nestedModalRef?.dismiss();
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }
}
